import { RangeSlider as SpectrumRangeSlider } from '@adobe/react-spectrum';
import { useEffect, useState } from 'react';
import { useRange } from 'react-instantsearch';

interface SliderValue {
  start: number;
  end: number;
}

interface AgeRangeFilterSliderProps {
  attributeFrom: string;
  attributeTo: string;
}

export function AgeRangeFilterSlider({ attributeFrom, attributeTo }: AgeRangeFilterSliderProps) {
  const { range: rangeFrom, refine: refineFrom, canRefine: canRefineFrom } = useRange({ attribute: attributeFrom });
  const { range: rangeTo, refine: refineTo, canRefine: canRefineTo } = useRange({ attribute: attributeTo });

  const dataMin = Math.min(rangeFrom.min ?? 0, rangeTo.min ?? 0);
  const dataMax = Math.max(rangeFrom.max ?? 0, rangeTo.max ?? 0);

  const [value, setValue] = useState<SliderValue>({ start: dataMin, end: dataMax });

  useEffect(() => {
    setValue({ start: dataMin, end: dataMax });
  }, [dataMin, dataMax]);

  const formatAgeLabel = (v: number) => `${Math.floor(v)}y`;

  const refineSafe = ({ start, end }: SliderValue) => {
    // Check if selected range overlaps actual data
    const hasOverlap = !(start > rangeFrom.max! || end < rangeTo.min!);

    if (!hasOverlap) {
      // No data in selected range — do not refine
      return;
    }

    // Clamp for Algolia only
    const safeStart = Math.min(Math.max(start, rangeFrom.min ?? dataMin), rangeFrom.max ?? dataMax);
    const safeEnd = Math.min(Math.max(end, rangeTo.min ?? dataMin), rangeTo.max ?? dataMax);

    refineFrom([safeStart, rangeFrom.max ?? dataMax]);
    refineTo([rangeTo.min ?? dataMin, safeEnd]);

    // Slider UI stays exactly where user dragged
    setValue({ start, end });
  };

  return (
    <SpectrumRangeSlider
      label="Select Age"
      minValue={dataMin}
      maxValue={dataMax}
      value={value}
      onChange={(v) => setValue(v)}       // free drag
      onChangeEnd={refineSafe}            // refine only if data exists
      isDisabled={!canRefineFrom && !canRefineTo}
      getValueLabel={({ start, end }) => `${formatAgeLabel(start)} - ${formatAgeLabel(end)}`}
    />
  );
}
